import { useEffect, useRef, useState } from 'react';
import { useNotification } from 'src/hooks/useNotification';
import { AUTH_STATUS_POLL_INTERVAL_MS, AUTH_STATUS_POLL_TIMEOUT_MS } from 'src/shared/constants';

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Waits for Zendesk OAuth consent after its consent page is opened, by re-fetching whatever was
// connected until it shows as authorized.
export const useAuthorizationPolling = () => {
  const notification = useNotification();
  const [isCheckingAuthStatus, setIsCheckingAuthStatus] = useState(false);
  const isMountedRef = useRef(true);
  const isPollingAuthStatusRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Calls `findAuthorized` every poll interval until it returns the authorized item. Resolves with
  // it, or null on timeout or when the component unmounts.
  const waitForAuthorization = async <T>(findAuthorized: () => Promise<T | null>) => {
    const deadline = Date.now() + AUTH_STATUS_POLL_TIMEOUT_MS;

    while (Date.now() < deadline) {
      await wait(AUTH_STATUS_POLL_INTERVAL_MS);
      if (!isMountedRef.current) return null;

      const authorized = await findAuthorized();
      if (!isMountedRef.current) return null;

      if (authorized) {
        return authorized;
      }
    }

    return null;
  };

  // Polls unless already polling, then passes the authorized item to `onAuthorized` and notifies
  // the result. Does nothing further if the component unmounted meanwhile.
  const pollAuthorization = async <T>(
    findAuthorized: () => Promise<T | null>,
    onAuthorized: (authorized: T) => void,
  ) => {
    if (isPollingAuthStatusRef.current) return;
    isPollingAuthStatusRef.current = true;
    setIsCheckingAuthStatus(true);

    const authorized = await waitForAuthorization(findAuthorized);
    isPollingAuthStatusRef.current = false;

    if (!isMountedRef.current) return;
    setIsCheckingAuthStatus(false);

    if (authorized) {
      onAuthorized(authorized);
      notification.success('Zendesk account was connected.');
    } else {
      notification.error('Zendesk authorization timed out. Please try again.');
    }
  };

  return { isCheckingAuthStatus, pollAuthorization };
};
